import { ChainType } from "@0xsquid/squid-types";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { ICantonContextData } from "../../core/providers/CantonProvider";
import { CantonWallet, ConnectWalletResult } from "../../core/types/wallet";
import { useWalletStore } from "../store/useWalletStore";
import { useCantonWallets } from "./useCantonWallets";

export function useCanton(): ICantonContextData {
  const disconnectWallet = useWalletStore((store) => store.disconnectWallet);
  const setConnectedWallet = useWalletStore(
    (store) => store.setConnectedWallet
  );

  const { wallets } = useCantonWallets();
  const connectedCantonWallet = useWalletStore(
    (store) => store.connectedWalletsByChainType[ChainType.CANTON]
  );
  const recentCantonWalletId = useWalletStore(
    (store) => store.recentConnectorIds[ChainType.CANTON]
  );

  const connectCanton = useMutation(
    async ({
      wallet,
    }: {
      wallet: CantonWallet;
    }): Promise<ConnectWalletResult<ChainType.CANTON>> => {
      let result;
      try {
        // Try to reconnect silently first (existing session, no popup)
        result = await wallet.connector.autoConnect();
      } catch {
        // Otherwise prompt the wallet to connect
        result = await wallet.connector.connect();
      }

      return {
        wallet,
        address: result.address,
      };
    }
  );

  const disconnectCanton = useCallback(async () => {
    await connectedCantonWallet.wallet?.connector.disconnect();
  }, [connectedCantonWallet.wallet?.connector]);

  const autoConnectCanton = useCallback(async () => {
    const recentCantonWallet = wallets.find(
      (w) => w.connectorId === recentCantonWalletId
    );
    if (!recentCantonWallet) {
      return;
    }

    const { address } = await recentCantonWallet.connector.autoConnect();

    setConnectedWallet(ChainType.CANTON, {
      wallet: recentCantonWallet,
      address,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets.map((w) => w.connectorId), recentCantonWalletId]);

  useEffect(() => {
    const wallet = connectedCantonWallet.wallet;

    if (!wallet) {
      return;
    }

    function onConnect(newAddress: string) {
      if (wallet) {
        setConnectedWallet(ChainType.CANTON, {
          wallet,
          address: newAddress,
        });
      }
    }

    function onDisconnect() {
      disconnectWallet(ChainType.CANTON);
    }

    wallet.connector.on("connect", onConnect);
    wallet.connector.on("disconnect", onDisconnect);

    return () => {
      wallet.connector.off("connect", onConnect);
      wallet.connector.off("disconnect", onDisconnect);
    };
  }, [disconnectWallet, setConnectedWallet, connectedCantonWallet.wallet]);

  return {
    connectCanton,
    disconnectCanton,
    autoConnectCanton,
    signer: connectedCantonWallet.wallet?.connector,
    wallets,
  };
}
